import { Preset, usePassThrough } from 'cc-prime';

const CustomPreset = usePassThrough(
  Preset,
  {
    breadcrumb: {
      root: 'mt-1 bg-transparent dark:bg-transparent border-none !p-0 overflow-y-hidden',
      menu: 'h-7',
      list: 'leading-normal',
      separator: 'w-2.5 h-2.5',
    },
    button: {
      root: ({ props }) => ({
        class: [
          {
            'w-full': props.fluid,
            '!text-base px-8 !py-2 min-h-14 min-w-14': props.size === 'large',
          },
        ],
      }),
      label: ({ props }) => ({
        class: {
          'flex-none': props.fluid,
        },
      }),
    },
    card: {
      root: 'dark:!bg-surface-800 shadow-none !rounded-3xl',
      body: 'p-4',
      content: '!p-0',
    },
    datatable: {
      root: '!rounded-3xl !overflow-hidden',
      bodyrow: ({ context, props }) => ({
        class: [
          {
            relative: props.selectionMode,
            'dark:hover:bg-surface-400/20 dark:hover:text-surface-300':
              props.selectionMode && !context.selected,
          },
        ],
      }),
      emptyMessageCell: 'bg-surface-0 dark:bg-surface-800',
      column: {
        bodycell: ({ props }) => ({
          class: [
            '!border-none',
            {
              '!-right-[1px]': props.alignFrozen === 'right',
            },
          ],
        }),
        headercell: ({ props }) => ({
          class: [
            '!bg-surface-0 dark:!bg-surface-800',
            {
              '!-right-[1px]': props.alignFrozen === 'right',
            },
          ],
        }),
      },
    },
    dialog: {
      root: '!rounded-3xl',
      header:
        '!bg-surface-100 dark:!bg-surface-900 !rounded-tl-3xl !rounded-tr-3xl ',
      content: ({ instance }) => ({
        class: [
          '!bg-surface-100 dark:!bg-surface-900',
          {
            '!rounded-tl-3xl !rounded-tr-3xl':
              !instance.$slots.header && !instance.$props.header,
          },
          { '!rounded-bl-3xl !rounded-br-3xl': !instance.$slots.footer },
        ],
      }),
      footer:
        '!bg-surface-100 dark:!bg-surface-900 !rounded-bl-3xl !rounded-br-3xl',
    },
    drawer: {
      header: 'bg-surface-100 dark:bg-surface-900',
      root: 'bg-surface-100 dark:bg-surface-900',
      footer: 'bg-surface-100 dark:bg-surface-900',
    },
    inputtext: {
      root: 'p-4 dark:!bg-surface-800 !rounded-xl !border-none',
    },
    inputnumber: {
      pcinput: {
        root: 'px-4 py-3.5 flex-auto m-0 text-surface-800 dark:text-white/80 placeholder:text-surface-400 dark:placeholder:text-surface-500 bg-surface-0 dark:bg-surface-900 border border-surface-300 dark:border-surface-700 invalid:focus:ring-red-200 invalid:hover:border-red-500 hover:border-primary focus:outline-none focus:outline-offset-0 focus:ring focus:ring-primary-500/50 dark:focus:ring-primary-400/50 focus:z-10 dark:!bg-surface-800 !rounded-xl !border-none',
      },
    },
    panel: {
      header: ({ instance }) => ({
        class: ['px-5 ', { 'py-2': instance.$slots.icons }],
      }),
    },
    popover: {
      root: 'rounded-3xl',
    },
    tag: {
      root: ({ props }) => ({
        class: [
          {
            '!text-primary-500 dark:!text-primary-100 !bg-primary-100 dark:!bg-primary-500':
              props.severity === 'primary',
            '!text-green-700 dark:!text-green-400 !bg-green-100 dark:!bg-green-950':
              props.severity === 'success',
            '!text-red-700 dark:!text-red-400 !bg-red-100 dark:!bg-red-950':
              props.severity === 'danger',
            '!text-sky-700 dark:!text-blue-400 !bg-sky-100 dark:!bg-blue-950':
              props.severity === 'info',
            '!text-orange-700 dark:!text-orange-400 !bg-orange-100 dark:!bg-orange-950':
              props.severity === 'warn',
            '!text-gray-700 dark:!text-gray-400 !bg-gray-100 dark:!bg-gray-800':
              props.severity === 'secondary',
          },
        ],
      }),
    },
    menu: {
      separator: {
        class: ['my-1 border-t border-surface-200 dark:border-surface-500'],
      },
    },
    select: {
      root: 'p-1 border-none !rounded-xl dark:!bg-surface-800',
      overlay: {
        class: ['max-w-[75vw] md:max-w-[40vw] !rounded-xl'],
      },
      option: {
        class: ['!whitespace-normal'],
      },
    },
    tablist: {
      root: {
        class: 'pr-2',
      },
    },
    tabs: {
      root: {
        class: ['rounded-3xl', '!bg-surface-0 dark:!bg-surface-800', 'p-2'],
      },
    },
    tab: {
      root: ({ context }) => ({
        class: [
          '!border-t-0 mb-0 !p-0 h-12',
          '!rounded-2xl mr-4 font-bold bg-transparent dark:bg-transparent p-0',
          {
            '!bg-primary-500 dark:!bg-highlight text-surface-0 dark:text-white/80':
              context.active,
          },
        ],
      }),
    },
    textarea: {
      root: 'p-4 dark:!bg-surface-800 !rounded-xl !border-none',
    },
    multiselect: {
      root: 'pl-2 h-12 items-center rounded-xl border-0 dark:!bg-surface-800',
      option: ({ context }) => ({
        class: [
          '!text-surface-700 dark:!text-white/80',
          {
            /* TODO: remove when multiselect preset is fixed */
            'selected-option': context.selected,
          },
        ],
      }),
    },
    /* TODO: remove when accordionheader preset is fixed */
    accordionheader: {
      root: {
        class: 'hover:!text-surface-800 dark:hover:!text-surface-200',
      },
    },
    inputotp: {
      root: {
        class:
          'flex items-center gap-2 text-2xl font-bold [&_[data-pc-name=pcinput]]:w-16 [&_[data-pc-name=pcinput]]:h-16 [&_[data-pc-name=pcinput]]:text-center',
      },
    },
  },
  {
    mergeSections: true,
    mergeProps: true,
  }
);

export default CustomPreset;
